import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Text } from '@constants/Themed';
import { Button, YStack } from 'tamagui';
import { BlurView } from 'expo-blur';
import { FontAwesome6 } from '@expo/vector-icons';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles, { responsivePadding } from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { HIDE_PASSWORD_RESET_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import apis, { noCreds } from '@api/api';
import loginUser from '@api/login';
import { loadLoginScreenStyles } from '@utils/loadWebStyles';
import { activateLoginArt } from '@navigation/loginArt/loginArtTransition';
import LoginArtBackground from '@navigation/screens/TopLevel/LoginScreen/Components/LoginArtBackground';
import { navigatePostEmailSignupToEasyMode } from './Components/Authorisation/utils/authUtils';

const FORM_MAX_WIDTH = 420;

const webGlassBackground = (lightTheme: boolean) =>
  lightTheme ? 'rgba(255, 255, 255, 0.42)' : 'rgba(18, 22, 38, 0.38)';

const webClassName = (className: string) => ({ className } as any);

type RouteParams = {
  email?: string;
  code?: string;
};

const SetPasswordScreen = () => {
  const { t } = useTranslation();
  const navigation = useNavigation<any>();
  const route = useRoute();
  const dispatch = useDispatch();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const { width } = useWindowDimensions();
  const isCompact = width < 720;

  const routeParams = (route.params || {}) as RouteParams;

  const initialEmail = useMemo(() => {
    if (routeParams.email) return String(routeParams.email).trim().toLowerCase();
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('email')?.trim().toLowerCase() || '';
    }
    return '';
  }, [routeParams.email]);

  const initialCode = useMemo(() => {
    if (routeParams.code) return String(routeParams.code).trim();
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('code')?.trim() || '';
    }
    return '';
  }, [routeParams.code]);

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(initialCode);
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [repeatHidden, setRepeatHidden] = useState(true);
  const [awaitingCode, setAwaitingCode] = useState(Boolean(initialCode));
  const [sendingCode, setSendingCode] = useState(false);
  const [saving, setSaving] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web') return;
      loadLoginScreenStyles();
      activateLoginArt();
    }, []),
  );

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
    if (initialCode) {
      setCode(initialCode);
      setAwaitingCode(true);
    }
  }, [initialEmail, initialCode]);

  const colors = useMemo(
    () => ({
      inputPlaceholder: theme ? 'rgba(100, 116, 139, 0.7)' : 'rgba(148, 163, 184, 0.75)',
      inputBackground: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
      inputBorder: theme ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.35)',
      inputText: theme ? '#0f172a' : '#e2e8f0',
      iconMuted: theme ? '#64748b' : '#94a3b8',
      title: theme ? '#0f172a' : '#f8fafc',
      subtitle: theme ? 'rgba(15, 23, 42, 0.72)' : 'rgba(226, 232, 240, 0.78)',
      primaryButton: '#3B82F6',
    }),
    [theme],
  );

  const inputContainerStyle = useMemo(
    () => [
      styles.loginInputContainer,
      styles.loginInputContainerWebStable,
      {
        backgroundColor: colors.inputBackground,
        borderColor: colors.inputBorder,
        width: '100%',
        maxWidth: FORM_MAX_WIDTH,
      },
    ],
    [colors.inputBackground, colors.inputBorder],
  );

  const inputStyle = useMemo(
    () => [styles.loginInput, { color: colors.inputText, width: '100%' }],
    [colors.inputText],
  );

  const goToLogin = useCallback(() => {
    navigation.navigate('Login');
  }, [navigation]);

  const handleSendCode = useCallback(async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: t('setPasswordInvalidEmail'), type: 'error' },
      });
      return;
    }
    setSendingCode(true);
    try {
      const result = await apis.passwordReset(trimmed);
      if (result?.success) {
        setAwaitingCode(true);
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.set('email', trimmed);
          window.history.replaceState({}, '', url.toString());
        }
      }
    } finally {
      setSendingCode(false);
    }
  }, [dispatch, email, t]);

  const handleSavePassword = useCallback(async () => {
    if (code.trim().length < 5) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: t('setPasswordInvalidCode'), type: 'error' },
      });
      return;
    }
    if (newPassword.length < 8) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: t('setPasswordTooShort'), type: 'error' },
      });
      return;
    }
    if (newPassword !== repeatPassword) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: t('setPasswordMismatch'), type: 'error' },
      });
      return;
    }

    setSaving(true);
    try {
      const res = await noCreds.post(
        '/validatePasswordReset',
        { code: code.trim(), newPassword },
        { validateStatus: () => true },
      );
      if (res.status !== 200) {
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: res.data?.error || t('setPasswordInvalidCode'),
            type: 'error',
          },
        });
        return;
      }
      dispatch({ type: HIDE_PASSWORD_RESET_MODAL });

      // Invite / init users: sign in immediately and enter Easy Mode onboarding
      const trimmedEmail = email.trim().toLowerCase();
      await new Promise<void>((resolve) => {
        store.dispatch(
          loginUser(
            { email: trimmedEmail, password: newPassword, rememberMe: true },
            async (data: any, success?: boolean) => {
              try {
                if (success && data?.userData?.id) {
                  await navigatePostEmailSignupToEasyMode(dispatch, navigation, {
                    ...data,
                    success: true,
                  });
                } else {
                  dispatch({
                    type: SHOW_AUTH_MODAL,
                    data: {
                      content: res.data?.message || t('setPasswordSave'),
                      type: 'success',
                    },
                  });
                  setTimeout(() => goToLogin(), 900);
                }
              } finally {
                resolve();
              }
            },
          ) as any,
        );
      });
    } catch (e) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: t('setPasswordInvalidCode'), type: 'error' },
      });
    } finally {
      setSaving(false);
    }
  }, [code, dispatch, email, goToLogin, navigation, newPassword, repeatPassword, t]);

  return (
    <YStack
      {...webClassName('login-root')}
      flex={1}
      width="100%"
      minWidth={320}
      style={{
        minHeight: '100vh' as any,
        position: 'relative',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh' as any,
      } as any}
    >
      {Platform.OS === 'web' ? <LoginArtBackground /> : null}

      <ScrollView
        contentContainerStyle={[
          localStyles.scrollContent,
          { paddingHorizontal: isCompact ? responsivePadding(16) : responsivePadding(32) },
        ]}
        keyboardShouldPersistTaps="handled"
        style={{ width: '100%', zIndex: 10 }}
      >
        <BlurView
          intensity={theme ? 58 : 48}
          tint={theme ? 'light' : 'dark'}
          {...webClassName('login-glassCard')}
          style={{
            width: '100%',
            maxWidth: isCompact ? 520 : 640,
            minWidth: 280,
            borderRadius: 24,
            overflow: 'hidden',
            backgroundColor: webGlassBackground(theme),
            padding: isCompact ? responsivePadding(24) : responsivePadding(40),
            borderWidth: 1,
            borderColor: theme ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.14)',
            alignSelf: 'center',
            ...(Platform.OS === 'web'
              ? {
                  boxShadow: theme
                    ? '0 8px 40px rgba(15, 23, 42, 0.10), inset 0 1px 0 rgba(255,255,255,0.55)'
                    : '0 8px 40px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
                }
              : null),
          } as any}
        >
          <YStack gap={responsivePadding(16)} width="100%" maxWidth={FORM_MAX_WIDTH} alignSelf="center">
            <Text style={[localStyles.title, { color: colors.title, fontSize: responsiveFontSize(28) }]}>
              {t('setPasswordTitle')}
            </Text>
            <Text style={[localStyles.subtitle, { color: colors.subtitle, fontSize: responsiveFontSize(15) }]}>
              {awaitingCode ? t('setPasswordCodeHint') : t('setPasswordEmailHint')}
            </Text>

            <View style={inputContainerStyle}>
              <TextInput
                style={inputStyle}
                placeholder={t('Email')}
                placeholderTextColor={colors.inputPlaceholder}
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                editable={!sendingCode && !saving}
                {...({ name: 'email', type: 'email' } as any)}
              />
            </View>

            {awaitingCode ? (
              <>
                <View style={inputContainerStyle}>
                  <TextInput
                    style={inputStyle}
                    placeholder={t('Verification Code')}
                    placeholderTextColor={colors.inputPlaceholder}
                    value={code}
                    onChangeText={setCode}
                    autoCapitalize="none"
                    autoCorrect={false}
                    maxLength={12}
                    editable={!saving}
                    {...({ name: 'code', autoComplete: 'one-time-code' } as any)}
                  />
                </View>

                <View style={inputContainerStyle}>
                  <TextInput
                    style={inputStyle}
                    placeholder={t('Password')}
                    placeholderTextColor={colors.inputPlaceholder}
                    value={newPassword}
                    onChangeText={setNewPassword}
                    secureTextEntry={passwordHidden}
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!saving}
                    {...({ name: 'new-password', type: passwordHidden ? 'password' : 'text' } as any)}
                  />
                  <TouchableOpacity
                    onPress={() => setPasswordHidden((v) => !v)}
                    style={[styles.loginPasswordToggle, styles.loginPasswordToggleWebStable]}
                    accessibilityRole="button"
                    accessibilityLabel={passwordHidden ? t('showPassword') : t('hidePassword')}
                  >
                    <FontAwesome6
                      style={{ color: passwordHidden ? colors.iconMuted : colors.primaryButton }}
                      name={passwordHidden ? 'eye' : 'eye-slash'}
                      size={20}
                    />
                  </TouchableOpacity>
                </View>

                <View style={inputContainerStyle}>
                  <TextInput
                    style={inputStyle}
                    placeholder={t('Repeat Password')}
                    placeholderTextColor={colors.inputPlaceholder}
                    value={repeatPassword}
                    onChangeText={setRepeatPassword}
                    secureTextEntry={repeatHidden}
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!saving}
                    onSubmitEditing={handleSavePassword}
                    {...({ name: 'repeat-password', type: repeatHidden ? 'password' : 'text' } as any)}
                  />
                  <TouchableOpacity
                    onPress={() => setRepeatHidden((v) => !v)}
                    style={[styles.loginPasswordToggle, styles.loginPasswordToggleWebStable]}
                    accessibilityRole="button"
                    accessibilityLabel={repeatHidden ? t('showPassword') : t('hidePassword')}
                  >
                    <FontAwesome6
                      style={{ color: repeatHidden ? colors.iconMuted : colors.primaryButton }}
                      name={repeatHidden ? 'eye' : 'eye-slash'}
                      size={20}
                    />
                  </TouchableOpacity>
                </View>

                <Button
                  backgroundColor={colors.primaryButton}
                  size="$4"
                  width="100%"
                  maxWidth={FORM_MAX_WIDTH}
                  disabled={saving}
                  onPress={handleSavePassword}
                  opacity={saving ? 0.7 : 1}
                >
                  <Text style={styles.loginSubmitButtonText}>{t('setPasswordSave')}</Text>
                </Button>

                <Pressable onPress={handleSendCode} disabled={sendingCode}>
                  <Text style={[localStyles.link, { color: colors.primaryButton, fontSize: responsiveFontSize(14) }]}>
                    {sendingCode ? t('setPasswordSending') : t('setPasswordResend')}
                  </Text>
                </Pressable>
              </>
            ) : (
              <Button
                backgroundColor={colors.primaryButton}
                size="$4"
                width="100%"
                maxWidth={FORM_MAX_WIDTH}
                disabled={sendingCode}
                onPress={handleSendCode}
                opacity={sendingCode ? 0.7 : 1}
              >
                <Text style={styles.loginSubmitButtonText}>
                  {sendingCode ? t('setPasswordSending') : t('setPasswordSendCode')}
                </Text>
              </Button>
            )}

            <Pressable onPress={goToLogin}>
              <Text style={[localStyles.link, { color: colors.subtitle, fontSize: responsiveFontSize(14) }]}>
                {t('setPasswordBackToLogin')}
              </Text>
            </Pressable>
          </YStack>
        </BlurView>
      </ScrollView>
    </YStack>
  );
};

const localStyles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: responsivePadding(32),
    minHeight: '100%' as any,
  },
  title: {
    fontWeight: '800',
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    lineHeight: responsiveFontSize(22),
    marginBottom: responsivePadding(8),
  },
  link: {
    textAlign: 'center',
    fontWeight: '600',
    marginTop: responsivePadding(4),
  },
});

export default SetPasswordScreen;
